import React from 'react';
import { DateTime } from 'luxon';
import { eFilterType, FilteredDataGrid, Badge, Currency, Grid, BodyText, Tooltip } from 'ui-toolkit';
import { IPageData } from 'types/rest/IPageData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPennant } from '@fortawesome/pro-solid-svg-icons';
import { IEscrowActualIncomingWireData } from '../../types/accounting/data/IEscrowActualIncomingWireData';
import { IEscrowActualIncomingWireQuery } from '../../types/accounting/query/IEscrowActualIncomingWireQuery';
import { ITableChangeState } from 'react-bootstrap-table-next';
import { calculateRemainingSplitValue } from '../../utilities/accounting/calculateRemainingSplitValue';
import { IEscrowExpectedIncomingWireData } from '../../types/accounting/data/IEscrowExpectedIncomingWireData';
import { IncomingTransactionsNavigation } from './IncomingTransactionsNavigation';
import { IsMatchedFilter } from './IsMatchedFilter';
import { cleanDataGridNames, decleanDataGridNames } from './cleanDataGridNames';
import { eMatchedQueryValue } from '../../types/accounting/eMatchedQueryValue';
import { AmountCents, BankAccountRow } from './SharedColumnDefinitions';

export interface IActualIncomingWiresDataGridProps {
  data?: IPageData<IEscrowActualIncomingWireData>;
  detail?: IEscrowActualIncomingWireData;
  isQueryFetching: boolean;
  query: IEscrowActualIncomingWireQuery;
  setSelectedId: (detail: string) => void;
  setQuery: (query: IEscrowActualIncomingWireQuery) => void;
}

export const ActualIncomingWiresSearchTable: React.FC<IActualIncomingWiresDataGridProps> = ({
  data,
  detail,
  isQueryFetching,
  query,
  setSelectedId,
  setQuery,
}) => {
  const isMatchedFilterItems: eMatchedQueryValue[] = [
    eMatchedQueryValue.UNMATCHED,
    eMatchedQueryValue.MATCHED,
    eMatchedQueryValue.MISMATCHED,
    eMatchedQueryValue.ALL,
  ];

  return (
    <>
      <Grid templateColumns="1fr max-content" alignItems="center">
        <div>
          <IncomingTransactionsNavigation transactionType="wires" />
        </div>
        <IsMatchedFilter
          matched={query.matched}
          onChange={(matched) => setQuery({ ...query, matched, pageNumber: 1 })}
          items={isMatchedFilterItems}
        />
      </Grid>
      {data && (
        <FilteredDataGrid
          columns={[
            {
              dataField: 'completedAtDate',
              filterDefault: query.completedAtDate,
              filterElement: eFilterType.DateRange,
              filterPlaceholder: 'Completed At',
              formatter: (column: any) => DateTime.fromISO(column as string).toFormat('D'),
              headerStyle: { width: '145px' },
              text: 'Completed At',
              sort: true,
            },
            {
              dataField: 'wireDetail.obi',
              filterDefault: query.obi,
              filterPlaceholder: 'OBI',
              filterElement: eFilterType.Text,
              headerStyle: { width: 'auto' },
              text: 'OBI',
            },
            {
              dataField: 'wireDetail.org',
              filterDefault: query.org,
              filterElement: eFilterType.Text,
              filterPlaceholder: 'Sender',
              headerStyle: { width: '200px' },
              text: 'Sender',
              sort: false,
            },
            BankAccountRow,
            AmountCents(query.amountCents),
            {
              dataField: 'unmatchedAmountCents',
              filterDefault:
                typeof query.unmatchedAmountCents === 'number'
                  ? query.unmatchedAmountCents.toString()
                  : query.unmatchedAmountCents,
              filterElement: eFilterType.Currency,
              filterPlaceholder: 'Unmatched',
              formatter: (_, row) => {
                const { amountCents, expectedIncomingWires: matches } = row as IEscrowActualIncomingWireData;
                const remainingAmount =
                  amountCents - (matches || []).reduce((sum, { amountCents }) => sum + amountCents, 0);
                return <Currency value={remainingAmount} valueIsCents={true} />;
              },
              headerStyle: { width: '120px' },
              text: 'Unmatched',
            },
            {
              dataField: 'expectedIncomingWires',
              formatter: (column, row) => {
                const fullyMatched =
                  calculateRemainingSplitValue(
                    (row && row.amountCents) || 0,
                    (column || []) as IEscrowExpectedIncomingWireData[]
                  ) === 0;
                const partiallyMatched = !!((column || []) as object[]).length && !fullyMatched;
                const mismatched =
                  row &&
                  row.expectedIncomingWires &&
                  !!row.expectedIncomingWires.length &&
                  row.accountNumber !== row.expectedIncomingWires[0].escrowFile.depositEscrowBankAccount.accountNumber;
                return (
                  <>
                    {fullyMatched && <Badge intent={'primary'}>matched</Badge>}
                    {partiallyMatched && <Badge intent={'secondary'}>partial</Badge>}
                    {mismatched && (
                      <Tooltip placement={'bottom'} tooltipContent={<BodyText>Bank account does not match</BodyText>}>
                        <FontAwesomeIcon icon={faPennant} color={'red'} />
                      </Tooltip>
                    )}
                    {!fullyMatched && !partiallyMatched && <Badge intent={'danger'}>unmatched</Badge>}
                  </>
                );
              },
              headerStyle: { width: '110px' },
              text: 'Status',
            },
          ]}
          data={data.content}
          keyField="id"
          loading={isQueryFetching}
          onTableChange={(type: string, tableState: ITableChangeState<IEscrowActualIncomingWireData>) => {
            const newFilters = Object.keys(tableState.filters).reduce(
              (p, key) => ({ ...p, [cleanDataGridNames(key)]: tableState.filters[key].filterVal }),
              {}
            );

            setQuery({
              ...query,
              ...newFilters,
              pageNumber: tableState.page,
              pageSize: tableState.sizePerPage,
              sortField: tableState.sortField || undefined,
              sortOrder: tableState.sortOrder || undefined,
            });
          }}
          remote={true}
          selectRow={{
            mode: 'radio',
            hideSelectColumn: true,
            onSelect: (row) => setSelectedId(row.id),
            clickToSelect: true,
          }}
          pagination={{
            page: query.pageNumber || 1,
            sizePerPage: query.pageSize || 10,
            totalSize: data.totalElements,
          }}
          sort={{ dataField: decleanDataGridNames(query.sortField) || 'UNSET', order: query.sortOrder || 'asc' }}
        />
      )}
    </>
  );
};
